import clsx from 'clsx'
import { SearchMenuStates } from 'components/Search/type'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import TextButton from 'LuxKit/Button/TextButton'
import Counter from 'LuxKit/Counter'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineSuitcaseIcon from 'LuxKit/Icons/line/LineSuitcaseIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const PaxAndLuggageInput = styled(TextButton)`
  justify-content: start;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  height: 100%;
  padding: 0 ${rem(16)};

  &.isOpen {
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground}
  }
`

const LabelWrapper = styled(Group)`
  min-width: ${rem(46)};
`

interface Props {
  onSelectionOpen: () => void
  onSelectionClose: () => void
}

function CarsAirportTransferPaxAndLuggageInputs(props: Props) {
  const { onSelectionClose, onSelectionOpen } = props

  const { activeMenu, airportTransferPassengers, airportTransferLuggage } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const intl = useIntl()

  const selectionButtonRef = useRef(null)

  const isOpen = activeMenu === SearchMenuStates.AirportTransferPaxAndLuggageSelection

  const onPaxCountChange = useCallback((value: number) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_PASSENGERS, passengers: value })
  }, [searchDispatch])

  const onLuggageCountChange = useCallback((value: number) => {
    searchDispatch({ type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_LUGGAGE, luggage: value })
  }, [searchDispatch])

  return <>
    <PaxAndLuggageInput
      kind="primary"
      variant="ghost"
      ref={selectionButtonRef}
      onClick={onSelectionOpen}
      className={clsx({ isOpen })}
    >
      <Group direction="horizontal" gap={8}>
        <LabelWrapper direction="horizontal" gap={4} verticalAlign="center">
          <LineUserIcon />
          <BodyText variant="medium">{airportTransferPassengers}</BodyText>
        </LabelWrapper>
        <LabelWrapper direction="horizontal" gap={4} verticalAlign="center">
          <LineSuitcaseIcon />
          <BodyText variant="medium">{airportTransferLuggage}</BodyText>
        </LabelWrapper>
      </Group>
    </PaxAndLuggageInput>
    <DropdownSheet
      size="content"
      open={isOpen}
      anchorRef={selectionButtonRef}
      triggerRef={selectionButtonRef}
      onClose={onSelectionClose}
      portaled
      placement="bottom-start"
      noPaddingOnBody
      autoHeight
    >
      <Sheet padding="20 20">
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" gap={20} horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'HsV6MwWu9npCvXx2nSgx',
                defaultMessage: 'Passengers',
                description: 'Label for Passengers input in airport transfer search bar',
              })}
            </BodyText>
            <Counter
              name="airport-transfer-search-passengers-counter"
              value={airportTransferPassengers}
              onChange={onPaxCountChange}
              data-testid="airport-transfer-search-passengers-counter"
              min={1}
            />
          </Group>
          <Group direction="horizontal" gap={20} horizontalAlign="space-between" verticalAlign="center">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'SyV0jWr4YoYfDYBgaOxM',
                defaultMessage: 'Luggage',
                description: 'Label for Luggage input in airport transfer search bar',
              })}
            </BodyText>
            <Counter
              name="airport-transfer-search-luggage-counter"
              value={airportTransferLuggage}
              onChange={onLuggageCountChange}
              data-testid="airport-transfer-search-luggage-counter"
            />
          </Group>
        </Group>
      </Sheet>
    </DropdownSheet>
  </>
}

export default CarsAirportTransferPaxAndLuggageInputs
